<template>
	<div v-if="infoCardTop" class="data-input-item-info-card-container" :style="`top: ${infoCardTop - 6}px`">
		<div @click.stop class="data-input-item-info-card display-flex flex-column">
			<div class="card-header-container display-flex flex-row flex-align-center flex-justify-space-between">
				<div>{{ stepNumber }}</div>
				<div class="tertiary-link-style" @click="openPreviewSettings">Preview settings</div>
			</div>
			<div class="display-flex flex-row flex-align-center">
				<InsertDataPreviewIcon :iconName="serviceSlug" class="mr-2" />
				{{ title }}
			</div>
			<div class="display-flex flex-row flex-align-center">
				<DataTypeIcon class="mr-1 data-type-icon" :itemValue="itemValue" />
				<div v-html="markSearchString(getCleanHtml(readableItemKey))" class="item-key-text px-1" />
			</div>
			<div v-if="!!formulaName" class="formula-indication display-flex flex-row flex-align-center py-2">
				<SIcon name="formula" class="mr-2" /> {{ formulaName }}
			</div>
			<div v-html="markSearchString(getCleanHtml(sampleData))" class="sample-data-text flex-align-center" />
		</div>
	</div>
</template>

<script>
import { sanitizeHtml } from '@/utils/helpers';
import { DataTypeEvaluator } from '@ply-io/data-types';

import { markString, escapeHTML } from '@/components/DataInput/DataInputMenu/helpers/helpers';
import InsertDataPreviewIcon from '@/components/DataInput/DataInputMenu/InsertDataPreviewIcon';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import DataInputMenuItemBaseMixin from '@/components/DataInput/DataInputMenu/MenuLists/helpers/DataInputMenuItemBaseMixin';
import { pluralize } from '@/utils/language-service';
const RESULTS_KEY = 'results';

import { FORMULAS } from '@/components/common/RichTextBox/FormulaChip/formulas.const';
const evaluator = new DataTypeEvaluator();

export default {
	name: 'DataItemInfoCard',
	components: { InsertDataPreviewIcon },
	mixins: [DataInputMenuItemBaseMixin],
	props: {
		item: { type: Object },
		itemKey: { type: String },
		infoCardTop: { type: Number },
		searchString: { type: String },
		stepIndex: { type: Number, default: 0 },
		itemValue: { type: [String, Number, Array, Object, Boolean] },
		formulaData: { type: Object },
	},
	computed: {
		stepNumber() {
			return `Step ${this.stepIndex + 1}`;
		},
		nodeInfo() {
			return this.item.nodeInfo || {};
		},
		serviceSlug() {
			return this.nodeInfo?.serviceSlug || this.nodeInfo?.service_slug;
		},
		title() {
			return this.nodeInfo.nodeTitle || this.nodeInfo.node_title || this.item.app.service_name;
		},
		sampleData() {
			const sampleData = this.dataType === 'array' ? `${this.itemValue.length} items` : this.itemValue;
			return `Sample: ${sampleData}`;
		},
		readableItemKey() {
			return this.getItemKeyTitle(this.item.key, this.getKey(this.itemKey));
		},
		itemNoun() {
			return this.nodeInfo?.noun;
		},
		dataType() {
			return evaluator.evaluate(this.itemValue);
		},
	},
	methods: {
		getCleanHtml(dirtyContent) {
			return sanitizeHtml(dirtyContent);
		},
		getKey(key) {
			if (key === RESULTS_KEY && this.itemNoun) {
				key = pluralize(this.itemNoun);
			}
			return escapeHTML(key);
		},
		openPreviewSettings() {
			AppDialogManagerService.openPreviewSettingsDialog();
		},
		markSearchString(str) {
			return markString(this.searchString, str);
		},
	},
	data() {
		const formulaName = this.formulaData?.functionName;
		return {
			formulaName: formulaName ? FORMULAS.find((formula) => formula.key === formulaName).title : null,
		};
	},
};
</script>

<style scoped lang="scss">
.data-input-item-info-card-container {
	display: none; // gets display flex from parent - to show
	background: transparent;

	.data-input-item-info-card {
		width: 250px;
		font-size: 10px;
		min-height: 90px;
		min-width: 250px;
		border-radius: 4px;
		padding: 6px 0 8px 0;
		background: var(--bg-color);
		box-shadow: var(--card-shadow);
		color: var(--primary-font-color);
		border: 1px solid var(--border-color);

		> div {
			padding: 3px 6px;
		}
		.data-type-icon {
			margin-top: 3px;
		}
		.item-key-text {
			background: var(--data-chip-background);
		}

		.formula-indication {
			border-top: var(--primary-border);
			background-color: var(--delicate-grey-background);
			overflow-wrap: anywhere;
		}
		.sample-data-text {
			padding-top: 11px;
			border-top: var(--primary-border);
			overflow-wrap: anywhere;
			color: var(--secondary-font-color);
		}
	}
}
</style>
